import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import api from '../../api';
import { toLocalInput, fromLocalInput } from '../../utils/timeclock';

// mode: 'edit' (office fixes), 'request' (staff asks), 'add' (office adds missed entry)
export const EntryDialog = ({ open, onClose, entry, mode, staff = [], visitTypes = [], onSaved }) => {
  const [cin, setCin] = useState(toLocalInput(entry?.clockIn));
  const [cout, setCout] = useState(toLocalInput(entry?.clockOut));
  const [reason, setReason] = useState('');
  const [staffId, setStaffId] = useState(staff[0]?.id || '');
  const [kind, setKind] = useState('shift');
  const [vtype, setVtype] = useState('Routine Visit');
  const [busy, setBusy] = useState(false);
  const title = { edit: 'Correct time', request: 'Request a time change', add: 'Add a missed entry' }[mode];

  const save = async () => {
    if (!cin) return toast.error('Enter the clock-in time');
    if (mode === 'add' && !cout) return toast.error('Enter the clock-out time');
    if (reason.trim().length < 3) return toast.error('Give a reason');
    setBusy(true);
    const body = { clockIn: fromLocalInput(cin), clockOut: fromLocalInput(cout), reason };
    try {
      if (mode === 'edit') await api.put(`/timeclock/entries/${entry.id}`, body);
      if (mode === 'request') await api.post(`/timeclock/entries/${entry.id}/request-change`, body);
      if (mode === 'add') await api.post('/timeclock/entries', { ...body, staffId, kind, visitType: kind === 'visit' ? vtype : null });
      toast.success(mode === 'request' ? 'Sent to the office for approval' : 'Saved');
      onSaved?.();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
    setBusy(false);
  };
  const field = 'w-full border rounded-lg px-2 py-2 text-sm';
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="time-entry-dialog">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {mode === 'add' && (
            <div className="grid grid-cols-2 gap-2">
              <select className={field} value={staffId} onChange={(e) => setStaffId(e.target.value)} data-testid="time-entry-staff">
                {staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
              <select className={field} value={kind} onChange={(e) => setKind(e.target.value)} data-testid="time-entry-kind">
                <option value="shift">Shift</option><option value="visit">Visit</option>
              </select>
              {kind === 'visit' && <select className={`${field} col-span-2`} value={vtype} onChange={(e) => setVtype(e.target.value)}>{visitTypes.map((v) => <option key={v}>{v}</option>)}</select>}
            </div>
          )}
          {entry && <p className="text-sm text-gray-600">{entry.staffName} · {entry.kind === 'visit' ? `${entry.visitType} – ${entry.patientName}` : 'Shift'}</p>}
          <label className="block text-sm">Clock in<input type="datetime-local" className={field} value={cin} onChange={(e) => setCin(e.target.value)} data-testid="time-entry-in" /></label>
          <label className="block text-sm">Clock out<input type="datetime-local" className={field} value={cout} onChange={(e) => setCout(e.target.value)} data-testid="time-entry-out" /></label>
          <label className="block text-sm">Reason (kept in the audit history)<textarea className={field} rows={2} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="time-entry-reason" /></label>
          <button disabled={busy} onClick={save} className="w-full py-2 rounded-lg bg-gray-900 text-[#d4af37] font-semibold disabled:opacity-50" data-testid="time-entry-save">
            {mode === 'request' ? 'Send request' : 'Save'}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
